import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ContentApi } from "../../../../shared/api/contentApi";
import { useContentContext } from "../../ContentContext";
import ParamValueField from "../../../../shared/paramEditor/ParamValueField";
import { normalizeValueByType } from "../../../../shared/paramEditor/normalizeValueByType";
import { parseOptionsJson } from "../../../../shared/paramEditor/parseOptionsJson";
import { notify } from "shared/utils/notify";
import DetailPageHero from "../../../../components/ui/DetailPageHero";
import DetailTabs, { DetailTabsContent } from "../../../../components/ui/DetailTabs";
import DetailPageSidebar from "../../../../components/ui/DetailPageSidebar";
import formatDate from "../../../../shared/helpers/formatDate";
import { Info, Database, Check, X, SquarePen, Trash, SquareArrowOutUpRight } from "lucide-react";
import PropTypes from "prop-types";
import DialogEditStrings from "../../_shared/DialogEditStrings";
import { Card, Select, Flex, Modal, Button, Form, Input, Splitter } from 'antd';
import EditStrings, { EditStringsRow } from "../../_shared/EditStrings";
import {
    DETAIL_CARD_CLASS,
    DETAIL_FIELD_CLASS,
    DETAIL_TEXTAREA_CLASS,
    YES_NO_OPTIONS,
} from "../../../../shared/constants/detailLayout";

function valueJsonToEditText(valueType, valueJson) {
    if (valueJson === null || typeof valueJson === "undefined") return "";
    const type = String(valueType || "").trim().toUpperCase();
    if (type === "BOOLEAN") return String(Boolean(valueJson));
    if (typeof valueJson === "object") return JSON.stringify(valueJson);
    return String(valueJson);
}

export default function ItemDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canMutate, selectedVersion, projectId, versionId } = useContentContext();

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [groups, setGroups] = useState([]);
    const [parameters, setParameters] = useState([]);
    const [values, setValues] = useState([]);

    const [newParameterId, setNewParameterId] = useState(undefined);
    const [newParameterValue, setNewParameterValue] = useState("");
    const [attachGroupId, setAttachGroupId] = useState(undefined);
    const [attachingGroup, setAttachingGroup] = useState(false);

    const [editingValueParameterId, setEditingValueParameterId] = useState("");
    const [editingValueText, setEditingValueText] = useState("");
    const [savingValueEdit, setSavingValueEdit] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const loadAll = async (signal) => {
        setLoading(true);
        setError("");
        try {
            const [entityData, entityValues, groupList, parameterList] = await Promise.all([
                ContentApi.getItemById(projectId, versionId, id, { signal }),
                ContentApi.getEntityValues(projectId, versionId, id, { signal }),
                ContentApi.getParameterGroups({ projectId, versionId, page: 1, pageSize: 1000 }, { signal }),
                ContentApi.getParametersPaged({ projectId, versionId, page: 1, pageSize: 1000 }, { signal }),
            ]);

            setItem(entityData || null);
            setValues(Array.isArray(entityValues?.items) ? entityValues.items : Array.isArray(entityValues) ? entityValues : []);
            setGroups(Array.isArray(groupList?.items) ? groupList.items : []);
            setParameters(Array.isArray(parameterList?.items) ? parameterList.items : []);
        } catch (e) {
            if (!signal?.aborted) {
                setError(e?.message || "Ошибка загрузки");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    useEffect(() => {
        if (!projectId || !versionId) {
            setItem(null);
            setGroups([]);
            setParameters([]);
            setValues([]);
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        loadAll(ac.signal);
        return () => ac.abort();
    }, [id, projectId, versionId]);

    const parameterById = useMemo(
        () => new Map(parameters.map((parameter) => [parameter.id || parameter.parameterId, parameter])),
        [parameters],
    );

    const currentGroup = useMemo(
        () => groups.find((group) => group.id === item?.parameterGroupId) || null,
        [groups, item?.parameterGroupId],
    );

    const availableGroups = useMemo(
        () => groups.filter((group) => group.id !== item?.parameterGroupId),
        [groups, item?.parameterGroupId],
    );

    const onAttachGroup = async () => {
        if (!canMutate || !attachGroupId) return;
        setAttachingGroup(true);
        try {
            await ContentApi.patchEntity(projectId, versionId, id, { parameterGroupId: attachGroupId });
            setAttachGroupId(undefined);
            await loadAll();
            notify.success("Группа параметров присоединена");
        } catch (e) {
            notify.error(e?.message || "Не удалось присоединить группу параметров");
        } finally {
            setAttachingGroup(false);
        }
    };

    const onSetValue = async () => {
        if (!canMutate || !newParameterId) return;
        try {
            const parameter = parameterById.get(newParameterId);
            const valueType = parameter?.valueTypeCode || parameter?.valueType;
            const normalized = normalizeValueByType(valueType, newParameterValue, parseOptionsJson(parameter?.optionsJson));
            if (!normalized.ok) {
                throw new Error(normalized.err || "Некорректное значение");
            }
            await ContentApi.setEntityValue(projectId, versionId, id, newParameterId, { valueJson: normalized.str, localizedTrId: null });
            setNewParameterValue("");
            await loadAll();
            notify.success("Значение параметра сохранено");
        } catch (e) {
            notify.error(e?.message || "Не удалось сохранить значение");
        }
    };

    const onStartEditValue = (valueRow) => {
        if (!canMutate) return;
        const parameter = parameterById.get(valueRow.parameterId);
        const valueType = parameter?.valueTypeCode || parameter?.valueType;
        setEditingValueParameterId(valueRow.parameterId);
        setEditingValueText(valueJsonToEditText(valueType, valueRow.valueJson));
    };

    const onCancelEditValue = () => {
        setEditingValueParameterId("");
        setEditingValueText("");
    };

    const onSaveEditValue = async (valueRow) => {
        if (!canMutate) return;
        try {
            const parameter = parameterById.get(valueRow.parameterId);
            const valueType = parameter?.valueTypeCode || parameter?.valueType;
            const normalized = normalizeValueByType(valueType, editingValueText, parseOptionsJson(parameter?.optionsJson));
            if (!normalized.ok) {
                throw new Error(normalized.err || "Некорректное значение");
            }
            setSavingValueEdit(true);
            await ContentApi.setEntityValue(projectId, versionId, id, valueRow.parameterId, {
                valueJson: normalized.str,
                localizedTrId: valueRow.localizedTrId ?? null,
            });
            onCancelEditValue();
            await loadAll();
            notify.success("Значение параметра обновлено");
        } catch (e) {
            notify.error(e?.message || "Не удалось обновить значение");
        } finally {
            setSavingValueEdit(false);
        }
    };

    const onDeleteValue = async (parameterId) => {
        if (!canMutate) return;
        try {
            await ContentApi.deleteEntityValue(projectId, versionId, id, parameterId);
            await loadAll();
            notify.success("Значение параметра удалено");
        } catch (e) {
            notify.error(e?.message || "Не удалось удалить значение");
        }
    };

    const onDeleteItem = async () => {
        try {
            await ContentApi.deleteEntity(projectId, versionId, id);
            notify.success("Предмет удален");
            navigate("/content/entity");
        } catch (e) {
            notify.error(e?.message || "Не удалось удалить предмет");
        }
    };

    const tabs = [
        { value: "overview", label: "Обзор", icon: <Info size={16} /> },
        { value: "usage", label: "Параметры", icon: <Database size={16} /> },
        { value: "meta", label: "Метаданные", icon: <Info size={16} /> },
    ];

    if (loading) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Загрузка...</div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="p-4 md:p-6">
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            </section>
        );
    }

    if (!item) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Не найдено</div>
            </section>
        );
    }

    return (
        <section className="w-full">
            <div className="mx-auto w-full max-w-6xl">
                {!canMutate && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 p-3 text-sm">
                        Версия {selectedVersion?.status || "unknown"} только для чтения. Используйте Fork в draft для изменений.
                    </div>
                )}
                <Flex gap="large" wrap="wrap" className="mt-2 w-full">
                    <div className="w-full">
                        <DetailTabs defaultValue="overview" tabs={tabs} onDelete={() => setDeleteDialogOpen(true)}>
                            <DetailTabsContent value="overview" className="space-y-4">
                                <DetailPageHero
                                    title={item.name}
                                    subtitle={item.systemName}
                                    breadcrumbs={[
                                        { label: "Предметы", to: "/content/entity/list" },
                                        { label: "Детали" },
                                    ]}
                                    metadata={[
                                        { label: "ID", value: `${item.id}` },
                                        { label: "Закреплено", value: `${item.isLocked ? "Да" : "Нет"}` },
                                    ]}
                                />
                                <EditStrings
                                    entity={item}
                                    projectId={projectId}
                                    versionId={versionId}
                                    onClose={() => setEditOpen(false)}
                                    onSaved={async (updated) => {
                                        setItem(updated || item);
                                        await loadAll();
                                        setEditOpen(false);
                                    }}
                                    title="Редактировать предмет"
                                    renameSystemName={ContentApi.renameItemSystemName}
                                    patchEntity={ContentApi.patchItem}
                                    getInitialExtraState={(entity) => ({
                                        optionJson: entity?.optionJson || null,
                                        isLocked: entity?.isLocked || false,
                                        orderIndexText: entity?.orderIndex != null ? String(entity.orderIndex) : "",
                                    })}
                                    buildPayload={({ name, description, extraState }) => ({
                                        name: name.trim(),
                                        description: description.trim() || null,
                                        optionsJson: extraState.optionsJson || null,
                                        isLocked: extraState.isLocked || false,
                                        orderIndex: (() => {
                                            const rawValue = String(extraState.orderIndexText || "").trim();
                                            if (!rawValue) return null;
                                            const parsed = Number.parseInt(rawValue, 10);
                                            return Number.isFinite(parsed) ? parsed : null;
                                        })(),
                                    })}
                                    renderExtraFields={({ extraState, setExtraField }) => (
                                        <>
                                            <Form.Item name="isLocked" className="mb-1">
                                                <EditStringsRow label="Закрепить" htmlFor="edit-strings-row-islocked">
                                                    <Select
                                                        id={`edit-strings-row-islocked`}
                                                        className={DETAIL_FIELD_CLASS}
                                                        value={Boolean(extraState.isLocked)}
                                                        onChange={(value) => setExtraField("isLocked", value)}
                                                        options={YES_NO_OPTIONS}
                                                    />
                                                </EditStringsRow>
                                            </Form.Item>
                                            <Form.Item name="optionsJson" className="mb-1">
                                                <EditStringsRow label="optionsJson" htmlFor="edit-strings-row-optionsjson">
                                                    <Input.TextArea
                                                        id={`edit-strings-row-optionsjson`}
                                                        className={`input ${DETAIL_TEXTAREA_CLASS}`}
                                                        rows={3}
                                                        value={extraState.optionsJson || ""}
                                                        onChange={(e) => setExtraField("optionsJson", e.target.value)}
                                                        placeholder="{}"
                                                    />
                                                </EditStringsRow>
                                            </Form.Item>
                                            <Form.Item name="orderIndex" className="mb-1">
                                                <EditStringsRow label="Order Index" htmlFor="edit-strings-row-orderindex">
                                                    <Input
                                                        id={`edit-strings-row-orderindex`}
                                                        type="number"
                                                        className={`input ${DETAIL_FIELD_CLASS}`}
                                                        value={extraState.orderIndexText || ""}
                                                        onChange={(e) => setExtraField("orderIndexText", e.target.value)}
                                                        placeholder="0"
                                                    />
                                                </EditStringsRow>
                                            </Form.Item>
                                        </>
                                    )}
                                />

                                <Card title="Дополнительно" className={DETAIL_CARD_CLASS + " mt-4"}>
                                    <div className="">
                                        <label className="eyebrow mb-2" htmlFor="edit-strings-row-optionsjson">OptionJson</label>
                                        {item.optionJson ? (
                                            <p className="text-sm" id="edit-strings-row-optionsjson">{JSON.stringify(item.optionJson)}</p>
                                        ) : (
                                            <p className="text-sm text-zinc-500">Нет</p>
                                        )}
                                    </div>
                                    <div className="mt-4">
                                        <label className="eyebrow mb-2" htmlFor="edit-strings-row-islocked">Заблокирован (isLocked)</label>
                                        {item.isLocked ? (
                                            <p className="text-sm" id="edit-strings-row-islocked">{item.isLocked ? "Да" : "Нет"}</p>
                                        ) : (
                                            <p className="text-sm text-zinc-500">Нет</p>
                                        )}
                                    </div>
                                    <div className="mt-4">
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                            <div>Создана: {item?.createdAt ? formatDate(item.createdAt) : "-"}</div>
                                            <div>
                                                Обновлено: {item?.updatedAt ? formatDate(item.updatedAt) : item?.createdAt ? formatDate(item.createdAt) : "-"}
                                            </div>
                                        </div>
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                            <div>Создана кем: {item?.createdBy ?? "-"}</div>
                                            <div>
                                                Обновлено кем: {item?.updatedBy ?? "-"}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </DetailTabsContent>
                            {/* Параметры, задать значение параметров */}
                            <DetailTabsContent value="usage" className="space-y-4">
                                <Splitter>
                                    <Splitter.Panel min="30%" className="p-2">
                                        <Card title="Значение параметров" className={DETAIL_CARD_CLASS}>
                                            <Flex gap="small" align="flex-start" wrap>
                                                <Select
                                                    value={newParameterId}
                                                    onChange={(value) => {
                                                        setNewParameterId(value);
                                                        setNewParameterValue("");
                                                    }}
                                                    placeholder="Выберите параметр"
                                                    className={DETAIL_FIELD_CLASS}
                                                    options={parameters.map((parameter) => ({
                                                        label: parameter.name || parameter.systemName || parameter.id,
                                                        value: parameter.id || parameter.parameterId,
                                                    }))}
                                                    disabled={!canMutate}
                                                />
                                                <div className={DETAIL_FIELD_CLASS}>
                                                    <ParamValueField
                                                        projectId={projectId}
                                                        versionId={versionId}
                                                        parameter={parameterById.get(newParameterId)}
                                                        value={newParameterValue}
                                                        onChange={setNewParameterValue}
                                                        disabled={!canMutate}
                                                    />
                                                </div>
                                            </Flex>
                                            <Flex justify="flex-start" className="mt-4">
                                                <Button
                                                    type="primary"
                                                    onClick={onSetValue}
                                                    disabled={!canMutate || !newParameterId}
                                                >
                                                    Сохранить значение
                                                </Button>
                                            </Flex>
                                            <Flex vertical gap="small" align="stretch" className="pt-6">
                                                {values.length === 0 ? (
                                                    <p className="text-sm text-zinc-500">Значения не заданы</p>
                                                ) : (
                                                    <ul className="space-y-2">
                                                        {values.map((valueRow) => {
                                                            const parameterId = valueRow.parameterId;
                                                            const parameter = parameterById.get(parameterId);
                                                            const isEditing = editingValueParameterId === parameterId;
                                                            return (
                                                                <li key={parameterId} className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-2">
                                                                    <Flex align="center" justify="space-between" gap="small" wrap>
                                                                        <div>
                                                                            <Link className="text-sm font-medium" to={`/content/parameters/${parameterId}`}>{parameter?.name || parameter?.systemName || parameterId}{<SquareArrowOutUpRight size={16} />}</Link>
                                                                            <div className="text-xs text-zinc-500">{parameter?.systemName || parameterId}</div>
                                                                        </div>
                                                                        <Flex gap="small" align="center">
                                                                            {isEditing ? (
                                                                                <>
                                                                                    <Button
                                                                                        type="primary"
                                                                                        onClick={() => onSaveEditValue(valueRow)}
                                                                                        loading={savingValueEdit}
                                                                                        disabled={!canMutate || savingValueEdit}
                                                                                        icon={<Check size={16} />}
                                                                                    >
                                                                                        Сохранить
                                                                                    </Button>
                                                                                    <Button icon={<X size={16} />} variant="outlined" color="default" onClick={onCancelEditValue} disabled={savingValueEdit}>
                                                                                        Отмена
                                                                                    </Button>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <Button
                                                                                        variant="text"
                                                                                        color="default"
                                                                                        onClick={() => onStartEditValue(valueRow)}
                                                                                        disabled={!canMutate}
                                                                                        icon={<SquarePen size={16} />}
                                                                                        placeholder="Редактировать значение"
                                                                                    >
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="text"
                                                                                        color="danger"
                                                                                        onClick={() => onDeleteValue(parameterId)}
                                                                                        disabled={!canMutate}
                                                                                        icon={<Trash size={16} />}
                                                                                        hint="Удалить значение"
                                                                                    >
                                                                                    </Button>
                                                                                </>
                                                                            )}
                                                                        </Flex>
                                                                    </Flex>
                                                                    {isEditing ? (
                                                                        <div className={`mt-2 ${DETAIL_FIELD_CLASS}`}>
                                                                            <ParamValueField
                                                                                projectId={projectId}
                                                                                versionId={versionId}
                                                                                parameter={parameter}
                                                                                value={editingValueText}
                                                                                onChange={setEditingValueText}
                                                                                disabled={!canMutate || savingValueEdit}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <pre className="mt-2 text-xs bg-zinc-50 dark:bg-zinc-900 p-2 rounded overflow-x-auto">
                                                                            {JSON.stringify(valueRow.valueJson, null, 2)}
                                                                        </pre>
                                                                    )}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </Flex>
                                        </Card>
                                    </Splitter.Panel>
                                    <Splitter.Panel  min="30%" className="p-2">
                                        <Card title="Присоединить группу" className={DETAIL_CARD_CLASS}>
                                            <h2 className="text-base font-medium">Группы параметров</h2>
                                            <Flex gap="small" align="center" wrap className="mt-2">
                                                <Select
                                                    className={DETAIL_FIELD_CLASS}
                                                    value={attachGroupId}
                                                    onChange={(value) => setAttachGroupId(value)}
                                                    placeholder={availableGroups.length ? "Выберите группу" : "Нет доступных групп"}
                                                    disabled={!canMutate || attachingGroup || availableGroups.length === 0}
                                                    options={availableGroups.map((group) => ({
                                                        label: group.name || group.systemName || group.id,
                                                        value: group.id,
                                                    }))}
                                                />
                                                <Button
                                                    type="primary"
                                                    onClick={onAttachGroup}
                                                    loading={attachingGroup}
                                                    disabled={!canMutate || attachingGroup || !attachGroupId}
                                                >
                                                    Присоединить
                                                </Button>
                                            </Flex>
                                            <Flex vertical gap="small" align="stretch" className="pt-6">
                                                {currentGroup ? (
                                                    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 space-y-1">
                                                        <div className="text-sm font-medium">{currentGroup.name || currentGroup.systemName}</div>
                                                        <div className="text-xs text-zinc-500">{currentGroup.systemName}</div>
                                                        <div className="text-xs text-zinc-500">ID: {currentGroup.id}</div>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-zinc-500">Группа не задана</p>
                                                )}
                                            </Flex>
                                        </Card>
                                    </Splitter.Panel>
                                </Splitter>
                            </DetailTabsContent>

                            <DetailTabsContent value="meta" className="space-y-4">
                                <Card title="Метаданные" className={DETAIL_CARD_CLASS}>
                                    <dl className="space-y-2 text-sm">
                                        <div>
                                            <dt className="eyebrow">ID</dt>
                                            <dd>{item.id}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">System Name</dt>
                                            <dd>{item.systemName}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">ValueTypeId</dt>
                                            <dd>{item.valueTypeId}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Group Parameter ID</dt>
                                            <dd>{item.parameterGroupId}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">NameTrId</dt>
                                            <dd>{item.nameTrId}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Description TrId</dt>
                                            <dd>{item.descriptionTrId}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Дата создания</dt>
                                            <dd>{formatDate(item.createdAt)}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Дата обновления</dt>
                                            <dd>{formatDate(item.updatedAt)}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Order Key</dt>
                                            <dd>{item.orderKey}</dd>
                                        </div>
                                    </dl>
                                </Card>
                            </DetailTabsContent>
                        </DetailTabs>
                    </div>
                </Flex>

                <DialogEditStrings
                    open={editOpen}
                    entity={item}
                    projectId={projectId}
                    versionId={versionId}
                    onClose={() => setEditOpen(false)}
                    onSaved={async (updated) => {
                        setItem(updated || item);
                        await loadAll();
                        setEditOpen(false);
                    }}
                    title="Редактировать предмет"
                    renameSystemName={ContentApi.renameItemSystemName}
                    patchEntity={ContentApi.patchItem}
                />

                {/* Delete Confirmation Dialog */}
                <Modal
                    open={deleteDialogOpen}
                    onCancel={() => setDeleteDialogOpen(false)}
                    title="Удалить сущность?"
                    closable={false}
                    centered
                    width=""
                    className=""
                    styles={{
                        body: { padding: 0 },
                        content: { overflow: "hidden" },
                    }}
                    onOk={() => { onDeleteItem(); setDeleteDialogOpen(false); }}
                    footer={(_, { OkBtn, CancelBtn }) => (
                        <>
                            <OkBtn />
                            <CancelBtn />
                        </>
                    )}
                >
                    <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                        Внимание! Удаление сущности может привести к ошибкам, которые его используют.
                    </div>
                    <p>
                        <strong>Сущность:</strong> {item?.name}
                    </p>
                    <p className="mt-1">
                        <strong>System Name:</strong> {item?.systemName}
                    </p>
                </Modal>

            </div>
        </section>
    );
}
